import { ChangeEvent, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import { MusicNoteOutlined, PlayArrowOutlined, UploadFileOutlined } from '@mui/icons-material';
import {
  NotificationSoundMelody,
  notificationSoundOptions,
  notificationSoundService,
} from '../../services/notificationSoundService';

const getError = (error: any) => error?.message || 'Не удалось применить настройки звука';

export const NotificationSoundSettingsPanel = () => {
  const [settings, setSettings] = useState(notificationSoundService.getSettings());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const customSoundLabel = useMemo(() => {
    if (!settings.customSoundName) return 'Файл не выбран';
    return `Выбран файл: ${settings.customSoundName}`;
  }, [settings.customSoundName]);

  const selectMelody = (event: ChangeEvent<HTMLInputElement>) => {
    const melody = event.target.value as NotificationSoundMelody;
    const nextSettings = { ...settings, melody };
    setSettings(nextSettings);
    notificationSoundService.saveSettings(nextSettings);
    setError('');
    setSuccess(true);
  };

  const changeVolume = (_event: Event, value: number | number[]) => {
    const volume = Array.isArray(value) ? value[0] : value;
    const nextSettings = { ...settings, volume };
    setSettings(nextSettings);
    notificationSoundService.saveSettings(nextSettings);
    setError('');
    setSuccess(true);
  };

  const uploadCustomSound = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');
    setSuccess(false);

    try {
      const nextSettings = await notificationSoundService.saveCustomSound(file);
      setSettings(nextSettings);
      setSuccess(true);
    } catch (e) {
      setError(getError(e));
    } finally {
      event.target.value = '';
    }
  };

  const testSound = async () => {
    setError('');
    const played = await notificationSoundService.play();
    if (!played) {
      setError('Браузер заблокировал звук. Нажмите кнопку проверки ещё раз или разрешите звук для сайта.');
      return;
    }
    setSuccess(true);
  };

  return (
    <Paper variant="outlined" sx={{ maxWidth: 920, p: { xs: 2, sm: 3 }, mt: 3 }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6">Звуковые уведомления</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Мелодия сохраняется на этом рабочем месте и используется для новых осмотров в очереди.
          </Typography>
        </Box>

        <FormControl>
          <RadioGroup value={settings.melody} onChange={selectMelody}>
            {notificationSoundOptions.map((option) => (
              <FormControlLabel
                key={option.id}
                value={option.id}
                control={<Radio />}
                label={(
                  <Box>
                    <Typography fontWeight={700}>{option.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{option.description}</Typography>
                  </Box>
                )}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography fontWeight={700}>Громкость уведомления</Typography>
            <Typography fontWeight={800}>{settings.volume}%</Typography>
          </Stack>
          <Slider
            value={settings.volume}
            min={0}
            max={100}
            step={5}
            marks={[
              { value: 0, label: '0%' },
              { value: 50, label: '50%' },
              { value: 100, label: '100%' },
            ]}
            onChange={changeVolume}
            aria-label="Громкость уведомления"
            sx={{ mt: 1 }}
          />
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button component="label" variant="outlined" startIcon={<UploadFileOutlined />}>
            Загрузить свой звук
            <input hidden type="file" accept="audio/*" onChange={uploadCustomSound} />
          </Button>
          <Button variant="contained" startIcon={<PlayArrowOutlined />} onClick={testSound}>
            Проверить звук
          </Button>
          <Stack direction="row" spacing={1} alignItems="center">
            <MusicNoteOutlined color="disabled" />
            <Typography color="text.secondary">{customSoundLabel}</Typography>
          </Stack>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">Настройки звука применены</Alert>}
      </Stack>
    </Paper>
  );
};
